import { useAuth } from "@/hooks/useAuth";
import { useEntitlementOverride } from "./entitlementFlip";

/**
 * Feature flag registry. Add new flags here; the `useFeature` predicate
 * supports multiple gate types (adminOnly today, proOnly/betaOnly later)
 * via a discriminated lookup so new flag shapes are additive.
 */
export const FEATURES = {
  tasksTab: {},
  voice_mode: { proOnly: true },
  // Family Profile: viewing + deleting stored facts is free; adding and
  // editing them is Pro-gated. Admin god-mode (adminPreview) unlocks it.
  familyProfileEdit: { proOnly: true },
} as const;

export type FeatureKey = keyof typeof FEATURES;

/**
 * Returns true only when the feature is enabled for this user AND
 * the relevant entitlement (admin status / Pro status) has been confirmed.
 * Returns false while auth/admin/pro state is still loading — never
 * flashes a gated feature to users who shouldn't see it.
 *
 * Admin preview: when the user is a real admin (is_admin) AND their
 * entitlements row has admin_preview = true, every feature unlocks
 * (free, pro, and in-progress). This is builder god-mode, flipped from
 * Supabase. It can never unlock anything for a non-admin.
 */
export const useFeature = (key: FeatureKey): boolean => {
  const { isAdmin, adminLoaded, isPro, proLoaded, adminPreview, loading } = useAuth();
  const entitlementOverride = useEntitlementOverride();
  const flag = FEATURES[key] as { adminOnly?: boolean; proOnly?: boolean };
  // Unconditioned flags (no adminOnly/proOnly) don't depend on any
  // entitlement lookup, so they resolve instantly instead of waiting on
  // session/adminLoaded/proLoaded — that wait was a visible pop-in for
  // flags like tasksTab that have no gate to resolve.
  if (!flag.adminOnly && !flag.proOnly) return true;
  if (loading || !adminLoaded || !proLoaded) return false;
  // Builder god-mode: real admin + preview flag on → see everything.
  if (isAdmin && adminPreview) return true;
  if (flag.adminOnly) return isAdmin;
  if (flag.proOnly) return isPro || entitlementOverride === true;
  return true;
};
